import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import emailApi from "../../api/emailApi";
import emailTemplateApi from "../../api/emailTemplateApi";
import FormField from "../ui/FormField";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../Button";
import Modal from "../ui/Modal";
import { Eye, X, Calendar } from "lucide-react";

const ScheduleForm = () => {
  const { state } = useLocation();
  const accountId = state?.accountId || null;
  const token = useSelector((s) => s.auth.access_token);
  const clients = useSelector((state) => state.account.accountDetails);
  const navigate = useNavigate();
  const defaultAccountId = accountId || clients?.[0]?.id || "";

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [form, setForm] = useState({
    accountId: defaultAccountId,
    emailTemplateId: "",
    frequency: "ONCE",
    scheduledDay: 1,
    scheduledMonth: 1,
    isEnabled: true,
    triggerType: "AUTO",
    customRecipientEmail: "",
  });

  useEffect(() => {
    if (!token) return;

    emailTemplateApi.getAllTemplates(token).then((res) => {
      const list = Array.isArray(res) ? res : res?.data || [];
      setTemplates(
        list
          .filter((t) => t.isActive !== false)
          .map((t) => ({
            label: t.templateName,
            value: t.id,
          }))
      );
    }).catch((err) => {
      console.error("Failed to load templates:", err);
      toast.error("Failed to load templates");
    });
  }, [token]);

  useEffect(() => {
    if (accountId) {
      setForm((prev) => ({ ...prev, accountId }));
    }
  }, [accountId]);

  const accountOptions =
    clients?.map((c) => ({
      label: c.accountName,
      value: c.id,
    })) || [];

  const handlePreview = async () => {
    if (!form.accountId || !form.emailTemplateId) {
      toast.warning("Please select both Account and Template to preview");
      return;
    }
    try {
      setPreviewLoading(true);
      const res = await emailApi.previewEmail(
        { accountId: form.accountId, templateId: form.emailTemplateId },
        token
      );
      setPreviewData(res);
      setPreviewOpen(true);
    } catch (err) {
      console.error("Preview error:", err);
      toast.error("Failed to load email preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.accountId || !form.emailTemplateId) {
      alert("Account and Template required");
      return;
    }

    const payload = {
      ...form,
      scheduledDay: Number(form.scheduledDay),
      scheduledMonth: Number(form.scheduledMonth),
    };

    try {
      setLoading(true);
      await emailApi.createSchedule(payload, token);
      toast.success("Schedule Created Successfully");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create schedule");
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplateName =
    templates.find((t) => t.value === form.emailTemplateId)?.label || "";
  const selectedAccountName =
    accountOptions.find((a) => a.value === form.accountId)?.label || "";

  return (
    <div className="bg-white p-6 rounded-xl shadow space-y-4 w-full h-[90vh]">
      <h2 className="font-bold text-lg text-slate-800">
        Create Email Schedule
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <FormField
          label="Account"
          type="dropdown"
          value={form.accountId}
          options={accountOptions}
          required
          disabled={!!accountId}
          onChange={(v) => setForm({ ...form, accountId: v })}
        />

        <FormField
          label="Template"
          type="dropdown"
          value={form.emailTemplateId}
          options={templates}
          required
          onChange={(v) => setForm({ ...form, emailTemplateId: v })}
        />

        <FormField
          label="Frequency"
          type="dropdown"
          value={form.frequency}
          options={["ONCE", "MONTHLY", "YEARLY"]}
          onChange={(v) => setForm({ ...form, frequency: v })}
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Day"
            type="number"
            min={1}
            max={31}
            value={form.scheduledDay}
            onChange={(v) => setForm({ ...form, scheduledDay: v })}
          />

          <FormField
            label="Month"
            type="number"
            min={1}
            max={12}
            value={form.scheduledMonth}
            onChange={(v) => setForm({ ...form, scheduledMonth: v })}
          />
        </div>

        <FormField
          label="Recipient Email"
          type="email"
          value={form.customRecipientEmail}
          onChange={(v) => setForm({ ...form, customRecipientEmail: v })}
          placeholder="metagradlabs@gmail.com"
        />
      </div>

      <div className="flex flex-row items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="bg-red-50 py-2 px-4 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-100"
        >
          Cancel
        </button>

        <button
          type="button"
          onClick={handlePreview}
          disabled={!form.accountId || !form.emailTemplateId || previewLoading}
          className="flex items-center gap-1.5 py-2 px-4 border border-indigo-300 rounded-md text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-50 transition"
        >
          {previewLoading ? <Spinner size="sm" /> : <Eye size={16} />}
          Preview
        </button>

        {loading ? (
          <Button label={<Spinner size={"sm"} />} />
        ) : (
          <Button label="Save" onClick={handleSubmit} />
        )}
      </div>

      <Modal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} title="Email Preview">
        {!previewData ? (
          <div className="text-center py-10 text-slate-500">Loading preview...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <div>
                  <span className="font-semibold text-slate-600">Account</span>
                  <p className="text-slate-800">{previewData.accountName}</p>
                </div>
                <div>
                  <span className="font-semibold text-slate-600">Template</span>
                  <p className="text-slate-800">{previewData.templateName}</p>
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <div>
                  <span className="font-semibold text-slate-600">Recipient</span>
                  <p className="text-slate-800">{form.customRecipientEmail || previewData.recipientEmail}</p>
                </div>
                <div>
                  <span className="font-semibold text-slate-600">Frequency</span>
                  <p className="text-slate-800">{form.frequency} &mdash; Day {form.scheduledDay}, Month {form.scheduledMonth}</p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-4 py-2 border-b">
                <p className="text-sm font-semibold text-slate-700">Subject: {previewData.subject}</p>
              </div>
              <div className="p-4 bg-white text-sm leading-relaxed max-h-[50vh] overflow-y-auto">
                {previewData.body && previewData.body.trim().startsWith("<") ? (
                  <div dangerouslySetInnerHTML={{ __html: previewData.body }} />
                ) : (
                  <div className="whitespace-pre-line">{previewData.body}</div>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ScheduleForm;
